import { and, eq, type InitialQueryBuilder, isNull, type Ref } from "@tanstack/db";

import type { WsScheduleTrigger } from "@storyboard/web-client";
import { store } from "@/store/store";
import type { ScheduleTrigger } from "@/store/types";

function transformWsScheduleTrigger(r: WsScheduleTrigger): ScheduleTrigger {
  return {
    id: r.id,
    oneTimeSchedule: r.oneTimeSchedule ?? null,
    cronSchedule: r.cronSchedule ?? null,
    workspaceId: r.workspaceId,
    broadcastActionId: r.broadcastActionId ?? null,
    createdAt: r.createdAt ?? null,
    updatedAt: r.updatedAt ?? null,
    timezone: r.timezone ?? null,
    enabled: r?.enabled ? 1 : 0,
  };
}

export async function bulkUpsertScheduleTrigger(records: WsScheduleTrigger[]) {
  if (records.length === 0) return;
  store.upsert("scheduleTrigger", records.map(transformWsScheduleTrigger));
}

/**
 * Every live trigger for the workspace, whether or not its time has passed.
 *
 * Deliberately carries no clock: `useLiveQuery` derives a query's identity from its IR, so a cutoff baked in here
 * would re-prepare the query every time the cutoff moved. `isUpcomingScheduleTrigger` asks that question at render
 * instead, where it costs nothing to ask again.
 */
export const scheduleTriggersQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ trigger: store.collection("scheduleTrigger") })
    .innerJoin({ action: store.collection("broadcastAction") }, ({ trigger, action }) =>
      eq(trigger.broadcastActionId, action.id),
    )
    .innerJoin({ workflowItem: store.collection("workflowItem") }, ({ action, workflowItem }) =>
      eq(action.workflowItemId, workflowItem.id),
    )
    .where(({ workflowItem }) => and(eq(workflowItem.workspaceId, workspaceId), isNull(workflowItem.deletedAt)))
    // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`; the annotation keeps the row type `T`.
    .select(({ trigger }): Ref<ScheduleTrigger> => trigger);

/**
 * Whether a trigger still has a send ahead of it at `now`.
 *
 * A recurring trigger always does. A one-time trigger does until its moment passes, and the boundary instant itself
 * still counts as upcoming. `now` is an ISO timestamp, compared against `oneTimeSchedule` as the string it is stored
 * as.
 */
export function isUpcomingScheduleTrigger(trigger: ScheduleTrigger, now: string): boolean {
  if (trigger.cronSchedule !== null) return true;
  return trigger.oneTimeSchedule !== null && trigger.oneTimeSchedule >= now;
}
